import React from "react";
import moment from "moment";

import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { RefreshButton } from "@/Common/Components/Buttons/RefreshButton";
import { ISortableItemRenderFunction, SortableItemContainerComponent } from "@/Common/Components/Collections/SortableItemContainer";
import { CheckboxControl } from "@/Common/Components/Inputs/CheckboxControl";
import { TextboxControl } from "@/Common/Components/Inputs/TextboxControl";
import { SettingsButton } from "@/Common/Components/Settings/SettingsButton";
import { SpanControl } from "@/Common/Components/SpanControl";
import { MyleTable } from "@/Common/Components/Tables/MyleTable";
import { MyleTableColumn, MyleTableColumnOptions } from "@/Common/Components/Tables/MyleTableColumn";
import { FileSelectionHelper } from "@/Common/Helpers/FileSelectionHelper";
import { FileType } from "@/Common/Models/FileType";
import { Person } from "@/Common/Models/Person/Person";
import { SortableItemUIModel } from "@/Common/Models/UI/SortableItemUIModel";
import { LuxonDateTimeParser } from "@/Common/Utilities/DateAndTime/LuxonDateTimeParser";
import { ExportableFileModel } from "@/ExportableFile/Models/ExportableFileTableItem";
import { FilePreviewComponentEditable } from "./FilePreviewComponentEditable";
import { FileReorderDisplay } from "./FileReorderDisplay";
import { FileSelectionSettingComponent, FileSelectionSettingComponentState } from "./FileSelectionSettingComponent";

import "./FileSelectionComponent.css";

interface IFileSelectionComponentProps {
    patient: Person;
    fileList: ExportableFileModel[];
    selectedFile: ExportableFileModel;
    checkedFiles: ExportableFileModel[];

    onFileSelected: (selected: ExportableFileModel) => void;
    onRefreshClicked: (setting: FileSelectionSettingComponentState) => void;
    onFilePreviewLoaded: () => void;
    onAllItemCheckboxChanged: (isChecked: boolean, itemIds?: number[]) => void;
    onItemCheckboxChanged: (item: ExportableFileModel, isChecked: boolean) => void;
    onSortDone: (item: ExportableFileModel, newIndex: number) => void;
}

class FileSelectionComponentState {
    public fileListToDisplay: ExportableFileModel[];
    public showSetting: boolean;
    public settingsContainerState: FileSelectionSettingComponentState;

    public showFilePreview: boolean;
    public showFilesReorder: boolean;
    public searchText: string;
    public isAllItemChecked: boolean;
}

class FileSelectionComponent extends BaseReactComponent<IFileSelectionComponentProps, FileSelectionComponentState> {
    private tableColumnsOptions: Immutable.List<MyleTableColumnOptions>;
    private saveOptionKey: string = "FilePreviewMain";

    constructor(props: IFileSelectionComponentProps) {
        super(props);

        const initialState = new FileSelectionComponentState();
        initialState.fileListToDisplay = [];
        initialState.showFilesReorder = true;
        this.state = initialState;
    }

    public componentDidUpdate(prevProps: IFileSelectionComponentProps): void {
        if (prevProps.fileList !== this.props.fileList) {
            this.mutateState(s => {
                s.fileListToDisplay = this.initFileListToDisplay(this.props.fileList, this.state.searchText);
            });
        }
    }

    public render(): JSX.Element {
        this.tableColumnsOptions = cacheStore.Helper.loadTableColumnOptionsFromSettings(this.saveOptionKey, this.getMyleTableColumnsDefaultOptions());

        const fileListWidth =
            this.showFilePreview() ?
                this.showFilesReorder() ?
                    "40%" : "60%" : "100%";
        const filePreviewWidth = this.showFilePreview() ? "40%" : "0%";
        const filesReorderWidth = this.showFilesReorder() ? "20%" : "0%";

        return (
            <div className="flex-container file-selection-component justifyContent-start">
                {this.renderFileList(fileListWidth)}
                {this.renderSeparator(() => this.mutateState(s => s.showFilePreview = !s.showFilePreview))}
                {this.renderFilePreview(filePreviewWidth)}
                {this.renderSeparator(() => this.mutateState(s => s.showFilesReorder = !s.showFilesReorder))}
                {this.renderFilesReorder(filesReorderWidth)}
            </div>
        );
    }

    private renderSeparator(onClick: React.MouseEventHandler): JSX.Element {
        return (
            <div
                className="flex-container aside-handle"
                style={{ "justifyContent": "center", "flexDirection": "column", "display": "flex" }}
                onClick={onClick}
            >
                <div className="slide-menu-icon blue">
                    <span /><span /><span />
                </div>
            </div>
        );
    }

    private renderFileList(fileListWidth: string): JSX.Element {
        const settingsPopupPosition = this.showFilePreview() ? "50%" : "0%";

        return (
            <div style={{ "width": fileListWidth }}>
                <div className="popup-attachment-left-head">
                    <div className="pull-right">
                        <div className="flex-container" style={{ justifyContent: "flex-start" }}>
                            <div>
                                <TextboxControl
                                    debounceMs={400}
                                    value={this.state.searchText}
                                    onChange={val => this.handleSearchTextChanged(val)}
                                    className="form-control popup-attachment-left-txtbox"
                                    placeholder={cacheStore.GetString("SearchLabel")}
                                    dataCy="fileSelectionComponent-search"
                                />
                            </div>
                            <div>
                                <RefreshButton rightMargin={5} onClick={() => this.props.onRefreshClicked(this.state.settingsContainerState)} />
                            </div>
                            <div>
                                <SettingsButton rightMargin={13} onClick={() => this.mutateState(s => s.showSetting = true)} />
                            </div>
                        </div>
                    </div>
                </div>

                {this.state.showSetting && (
                    <FileSelectionSettingComponent
                        defaultState={this.state.settingsContainerState}
                        className="filter-component popup-attachment-settingsPopup position"
                        style={{ marginRight: settingsPopupPosition }}
                        onClose={containerState => this.reload(containerState)}
                        onOk={containerState => this.reload(containerState)}
                    />
                )}

                <div className="fxheightpopup flex-container" data-cy="fileSelection-list">
                    <MyleTable
                        TableRenderOptions={MyleTable.LoadDefaultTableRenderOptions(this.tableColumnsOptions)}
                        Columns={this.getTableColumns(this.tableColumnsOptions)}
                        TableId="filePreview_table"
                        DataSource={this.state.fileListToDisplay.toImmutable()}
                        RowClickOperation={null}
                        RowClickFunction={item => this.handleOnRowClick(item)}
                        SaveOptionskeyTarget={this.saveOptionKey}
                        IsActive={(item: ExportableFileModel) => this.props.selectedFile?.itemId === item?.itemId}
                    />
                </div>

            </div>
        );
    }

    private renderFilePreview(filePreviewWidth: string): JSX.Element {
        if (!this.showFilePreview())
            return null;

        return (
            <div className="flex-container file-preview-container" style={{ "width": filePreviewWidth, alignItems: "normal" }}>
                <div>
                    <div className="flex-container">
                        <div className="flex-container" style={{ "flexDirection": "column", "justifyContent": "flex-start" }}>
                            <div />
                        </div>
                    </div>
                </div>

                <div className="popup-attachment-right-body">
                    <FilePreviewComponentEditable
                        file={this.props.selectedFile.Model}
                        onFilePreviewLoaded={this.props.onFilePreviewLoaded}
                    />
                </div>
            </div>
        );
    }

    private renderFilesReorder(filesReorderWidth: string): JSX.Element {
        if (!this.showFilesReorder())
            return null;

        const sortableFileRenderFunction: ISortableItemRenderFunction<ExportableFileModel> =
            (file: ExportableFileModel, idx: number): JSX.Element => (
                <div key={"id_" + idx} className="sortable-file-container">
                    <FileReorderDisplay
                        name={file.Name}
                        date={LuxonDateTimeParser.parse(file.Model.date)?.toFormat("yyyy-MM-dd")}
                        type={FileSelectionHelper.determineTypeName(file.Model.fileType)}
                        onRowClick={() => this.handleOnRowClick(file)}
                        onClose={() => this.props.onItemCheckboxChanged(file, false)}
                        isSelected={this.props.selectedFile?.itemId === file.itemId}
                    />
                </div>
            );

        const exportableFilesItemUIModels = Immutable.List(this.props.checkedFiles
            .map((model, index) => new SortableItemUIModel<ExportableFileModel>({
                ItemId: model.itemId.toString(),
                ItemIndex: index,
                Model: model
            })));

        return (
            <div className="file-selection-section" style={{ "width": filesReorderWidth }}>
                <h3 className="file-selection-label">{cacheStore.GetString("FilesSelectionLabel")}</h3>
                <div className="files-reorder-container">
                    <SortableItemContainerComponent
                        ComponentId="fileReorderSelectionContainer"
                        DragGroupClass="draggroup-filereorder"
                        ItemRenderingFunction={sortableFileRenderFunction}
                        SortableItems={exportableFilesItemUIModels}
                        OnSortDone={this.props.onSortDone}
                        DragStartDelegate={this.dragStartForSingleDropZone}
                        DragStopDelegate={this.dragStopForSingleDropZone}
                    />
                </div>
            </div>
        );
    }

    private handleOnRowClick(item: ExportableFileModel): void {
        if (item.itemId === this.props.selectedFile?.itemId)
            return;

        this.mutateState(
            s => s.showFilePreview = true,
            () => this.props.onFileSelected?.(item)
        );
    }

    private handleSearchTextChanged(value: string): void {
        this.mutateState(s => {
            s.searchText = value;
            s.fileListToDisplay = this.initFileListToDisplay(this.props.fileList, value);
        });
    }

    private initFileListToDisplay(fileList: ExportableFileModel[], searchText?: string): ExportableFileModel[] {
        let fileListToDisplay = fileList || [];
        if (!IsNullOrWhiteSpace(searchText)) {
            fileListToDisplay = fileListToDisplay.filterSearchAdvanced(
                searchText,
                (IsMatchingSearchTextFct, item) => {
                    let cleanDate = (item.Model.date && item.Model.date.replace(/-/g, "")) || "";

                    return IsMatchingSearchTextFct(item.Name) ||
                        IsMatchingSearchTextFct(FileSelectionHelper.determineTypeName(item.Model.fileType)) ||
                        IsMatchingSearchTextFct(cleanDate) ||
                        IsMatchingSearchTextFct(item.Model.date);
                }
            );
        }

        return fileListToDisplay
            .sortByPropertyDesc(x => {
                if (x.Model.fileType == FileType.MEDICALSUMMARY)
                    return Number.MAX_VALUE;
                if (x.Model.fileType == FileType.MEDICATIONSUMMARY)
                    return Number.MAX_VALUE - 1;
                return moment(x.Model.date).unix();
            });
    }

    private reload(settingsContainerState: FileSelectionSettingComponentState): void {
        this.mutateState(
            s => {
                s.settingsContainerState = settingsContainerState;
                s.showSetting = false;
            },
            () => {
                if (settingsContainerState && this.props.onRefreshClicked)
                    this.props.onRefreshClicked(settingsContainerState);
            }
        );
    }

    private getTableColumns(columnsOptions: Immutable.List<MyleTableColumnOptions>): Immutable.List<MyleTableColumn> {
        return MyleTableColumn.buildList<ExportableFileModel>(
            columnsOptions,
            column => {
                switch (column.FieldName) {
                    case "Checked":
                        return {
                            renderHeader: () => (
                                <div>
                                    <CheckboxControl
                                        id="FilesAllCheckedPopup"
                                        checked={this.state.isAllItemChecked}
                                        onChange={() => this.mutateState(
                                            s => {
                                                s.fileListToDisplay = this.state.fileListToDisplay.map(x => {
                                                    x.isChecked = !this.state.isAllItemChecked;
                                                    return x;
                                                });
                                                s.isAllItemChecked = !this.state.isAllItemChecked;
                                            },
                                            () => this.props.onAllItemCheckboxChanged?.(this.state.isAllItemChecked, this.state.fileListToDisplay.map(x => x.itemId))
                                        )}
                                    />
                                </div>
                            ),
                            renderCell: item => (
                                <div>
                                    <CheckboxControl
                                        checked={item.isChecked}
                                        onChange={() => {
                                            const isChecked = !item.isChecked;
                                            this.mutateState(
                                                s => s.fileListToDisplay = s.fileListToDisplay.mutate(
                                                    x => x.isChecked = isChecked,
                                                    x => x.itemId == item.itemId
                                                ),
                                                () => this.props.onItemCheckboxChanged?.(item, isChecked)
                                            );
                                        }}
                                        disabled={item.isDisabled}
                                    />
                                </div>
                            )
                        };

                    case "Description":
                        return {
                            renderCell: item => (
                                <div><SpanControl text={item.Model.description} /></div>
                            )
                        };

                    case "Date":
                        return {
                            renderCell: item => {
                                let cleanDate = "";
                                if (item.Model.date != null)
                                    cleanDate = moment(item.Model.date).format("YYYY-MM-DD");
                                return (<div><SpanControl text={cleanDate} /></div>);
                            }
                        };

                    case "Type":
                        return {
                            renderCell: item => (
                                <div><SpanControl text={FileSelectionHelper.determineTypeName(item.Model.fileType)} /></div>
                            )
                        };
                }
            }
        );
    }

    private getMyleTableColumnsDefaultOptions(): MyleTableColumnOptions[] {
        return [
            new MyleTableColumnOptions({
                HeaderText: "",
                FieldName: "Checked",
                IsFixedInPlace: true,
                RowClickEnabled: false,
                IsSortable: false,
                WidthPixels: 75
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("DescriptionLabel"),
                FieldName: "Description",
                IsFixedInPlace: true,
                RowClickEnabled: true,
                IsSortable: false,
                WidthPercentageDefault: 50
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("DateLabel"),
                FieldName: "Date",
                RowClickEnabled: true,
                IsSortable: false,
                WidthPercentageDefault: 20
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("TypeLabel"),
                FieldName: "Type",
                RowClickEnabled: true,
                IsSortable: false,
                WidthPercentageDefault: 20
            })
        ];
    }

    private showFilesReorder(): boolean {
        return this.props.checkedFiles?.any() && this.state.showFilesReorder;
    }

    private showFilePreview(): boolean {
        return this.props.selectedFile && this.state.showFilePreview;
    }

    private dragStartForSingleDropZone(event: JQueryEventObject, ui: JQueryUI.SortableUIParams): void {
        ui.item.closest(".drag-container").addClass("over");
    }

    private dragStopForSingleDropZone(event: JQueryEventObject, ui: JQueryUI.SortableUIParams): void {
        ui.item.closest(".drag-container").removeClass("over");
    }
}

export { FileSelectionComponent };
